import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Loader2, MailQuestion, X } from "lucide-react";
import { fmtDate } from "@/lib/format";

export const useStatementRequests = (refreshKey) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/statement-requests").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const find = (accId, start, end) => items.find((r) => r.bank_account_id === accId && r.start <= end && r.end >= start);
  return { items, find, reload: load };
};

export function RequestStatementButton({ accountId, start, end, request, onDone, testid }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  if (request) {
    const cancel = async () => {
      if (!confirming) { setConfirming(true); setTimeout(() => setConfirming(false), 4000); return; }
      try { await api.post(`/statement-requests/${request.id}/cancel`); setConfirming(false); toast.success("Request cancelled"); onDone(); }
      catch (err) { toast.error(err?.response?.data?.detail || "Couldn't cancel"); }
    };
    return (
      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1 whitespace-nowrap"
        style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #93C5FD" }}
        title={`Requested from ${request.client_names.join(", ")} · ${request.reminder_count} reminder(s) sent`} data-testid={`${testid}-requested`}>
        <Clock size={10} /> Requested {fmtDate(request.created_at)}
        <button onClick={cancel} className="ml-0.5 hover:text-[var(--ll-error)] inline-flex items-center gap-0.5" aria-label="Cancel request" data-testid={`${testid}-cancel`}>
          <X size={10} />{confirming && <span className="normal-case tracking-normal">Click again to cancel</span>}
        </button>
      </span>
    );
  }
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/statement-requests", { bank_account_id: accountId, start, end });
      toast.success(`Asked ${r.data.client_names.join(", ")} for ${r.data.range_text}`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the request"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={send} disabled={busy} data-testid={testid}
      title="Send the client a portal message asking for this statement. Reminders go out every 3 days until it's uploaded."
      className="text-[10px] px-1.5 py-0.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1 whitespace-nowrap transition-colors disabled:opacity-50"
      style={{ borderColor: "var(--ll-border)" }}>
      {busy ? <Loader2 size={10} className="animate-spin" /> : <MailQuestion size={10} />} Request from client
    </button>
  );
}

export function ClientRequestedStatements({ onPick }) {
  const { items } = useStatementRequests(0);
  if (!items.length) return null;
  return (
    <div className="mb-4 p-3 border-l-4" style={{ background: "#EFF6FF", borderColor: "#3B82F6" }} data-testid="client-requested-statements">
      <div className="text-sm font-medium mb-1.5 flex items-center gap-1.5"><MailQuestion size={14} /> Your accountant is waiting for:</div>
      <ul className="space-y-1">
        {items.map((r) => (
          <li key={r.id} className="text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`client-request-${r.id}`}>
            <span><b>{r.account_label}</b> · {r.range_text}</span>
            <button onClick={() => onPick(r.bank_account_id)} className="text-xs px-2 py-1 border bg-white hover:bg-[var(--ll-mint)]"
              style={{ borderColor: "var(--ll-border)" }} data-testid={`client-request-pick-${r.id}`}>Upload for this account</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
